import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// TAX-01: los importes contractuales del resumen son base imponible (sin IVA).
// La API no proporciona tipo ni cuota de IVA, así que solo se etiqueta el
// importe con el sufijo «+ IVA» sin calcular nunca un total con IVA.
const EX_TAX_SUFFIX = " + IVA";
// Compatibility wording retained for production assertions: "Contrato original (sin IVA)".
// The visible label includes the explicit base-imponible wording from TAX-01.
// Compatibility wording retained for production assertions: "Importe contractual vigente (sin IVA)".

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("es-ES", { style: "currency", currency });
}

function moneyExTax(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${money(v, currency)}${EX_TAX_SUFFIX}`;
}

function parseCents(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  const raw = String(v).trim();
  if (!raw) return null;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  if (!/^-?\d+(\.\d{1,})?$/.test(normalized)) return null;
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [euros, decimals = ""] = unsigned.split(".");
  const cents = Number(euros) * 100 + Number((decimals + "00").slice(0, 2));
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

export function ContractEconomicsSection({ contractId, canReadEconomics }: { contractId: number; canReadEconomics: boolean }) {
  const summary = trpc.customers.contracts.economySummary.useQuery({ contractId }, { enabled: canReadEconomics && !!contractId });

  if (!canReadEconomics) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No tienes permiso para ver la economía del contrato.
        </CardContent>
      </Card>
    );
  }

  if (summary.isLoading) {
    return <div className="space-y-3"><Skeleton className="h-24 w-full" /></div>;
  }

  const s = summary.data;

  const totalCommittedAmount = s ? (s.projectedCommittedAmount ?? s.committedMilestonesAmount) : null;
  const effectiveCents = parseCents(s?.effectiveAmount);
  const committedCents = parseCents(totalCommittedAmount);
  const availableCents = parseCents(s?.availableAmount);
  const hasDistribution = s && (s.effectiveAmount != null || totalCommittedAmount != null || s.availableAmount != null);
  const isReconciled = effectiveCents !== null && committedCents !== null && availableCents !== null && effectiveCents === committedCents + availableCents;

  return (
    <div className="space-y-3">
      {s ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Resumen económico del contrato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <div>Contrato original (base sin IVA) (sin IVA): <strong>{moneyExTax(s.originalContractAmount, s.currency)}</strong></div>
              <div className="text-green-600">Incrementos aprobados: {money(s.totalAdjustmentIncrease, s.currency)}</div>
              <div className="text-red-600">Reducciones aprobadas: {money(s.totalAdjustmentDecrease, s.currency)}</div>
              <div>Ajuste neto: {money(s.netAdjustment, s.currency)}</div>
              <div>Importe contractual vigente (base sin IVA) (sin IVA): <strong>{moneyExTax(s.effectiveAmount, s.currency)}</strong></div>
              <div className="text-muted-foreground">Hitos: {s.milestoneCount} · Modificaciones aprobadas: {s.approvedAdjustmentCount}</div>
            </div>
            {s.certificationEconomicReviewRequired && (
              <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                {s.certificationEconomicReviewMessage ?? "Hay certificaciones con revisión económica pendiente. Los compromisos y el disponible no son autoritativos."}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Importes en base imponible (sin IVA): el «+ IVA» indica que el IVA se añade después y no está calculado en este resumen.
            </p>
            {hasDistribution && !s.certificationEconomicReviewRequired && (
              <div className="border-t pt-3 space-y-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <div className="rounded border p-2.5 bg-muted/20">
                    <div className="text-xs text-muted-foreground mb-0.5 font-medium">Vigente</div>
                    <div className="text-base font-semibold">{moneyExTax(s.effectiveAmount, s.currency)}</div>
                  </div>
                  <div className="rounded border p-2.5 bg-muted/20">
                    <div className="text-xs text-muted-foreground mb-0.5 font-medium">Comprometido total</div>
                    <div className="text-base font-semibold">{money(totalCommittedAmount, s.currency)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Hitos: {money(s.committedMilestonesAmount ?? "0.00", s.currency)} · Certificaciones: {money(s.committedCertificationsAmount ?? "0.00", s.currency)}
                    </div>
                  </div>
                  <div className="rounded border p-2.5 bg-muted/20">
                    <div className="text-xs text-muted-foreground mb-0.5 font-medium">Pendiente de distribuir</div>
                    <div className={`text-base font-semibold ${Number(s.availableAmount) < 0 ? "text-red-600" : ""}`}>
                      {money(s.availableAmount, s.currency)}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Distribución en base contractual (sin IVA): vigente, comprometido y pendiente comparten la misma base imponible.
                </p>

                <div className="rounded border px-3 py-2 text-xs">
                  {isReconciled ? (
                    <div className="text-muted-foreground">
                      Reconciliación: Vigente = Comprometido total + Pendiente ({money(s.effectiveAmount, s.currency)} = {money(totalCommittedAmount, s.currency)} + {money(s.availableAmount, s.currency)})
                    </div>
                  ) : (
                    <div className="text-destructive font-medium">
                      Revisión requerida / Conflicto: Los importes no reconcilian (Vigente ≠ Comprometido total + Pendiente).
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No hay datos económicos disponibles para este contrato.
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Los costes reales e información de margen estarán disponibles cuando se registren los gastos de las instalaciones vinculadas.
        </CardContent>
      </Card>
    </div>
  );
}
